/**
 * Helpers for selecting and rendering promotions on the price list PDF.
 *
 * Promotion rows are joined with product, product_type, customer_type, price_tier
 * (see fetchPromotions in supabase-client.js). NULL FK fields mean "applies to all".
 */

import { toLocalDateString } from './dateUtils'

/**
 * Filter to promotions that are currently running or scheduled in the future.
 */
export function filterCurrentAndUpcoming(promotions) {
  const today = toLocalDateString(new Date())
  return promotions.filter(p => {
    if (p.active === false) return false
    if (!p.end_date) return true
    return p.end_date >= today
  })
}

/**
 * Filter to promotions relevant to the given price tier names and (optionally) customer type.
 * NULL tier_id or customer_type_id means the promo applies to all.
 */
export function filterByTierAndCustomerType(promotions, { tierNames, customerTypeId = null }) {
  return promotions.filter(p => {
    const tierMatch = !p.price_tier_id || (p.price_tier?.name && tierNames.has(p.price_tier.name))
    const ctMatch = !customerTypeId || !p.customer_type_id || p.customer_type_id === customerTypeId
    return tierMatch && ctMatch
  })
}

/**
 * Human-readable scope: "Oyster · Premium" / "All products" / "Shiitake".
 */
function promoScope(p) {
  const parts = []
  if (p.product?.strain) parts.push(p.product.strain)
  if (p.product_type?.name) parts.push(p.product_type.name)
  return parts.length ? parts.join(' · ') : 'All products'
}

/**
 * Format dd/mm – dd/mm or "until dd/mm" for the PDF.
 */
function promoValidity(p) {
  const fmt = (d) => {
    if (!d) return null
    const [, m, day] = d.split('-')
    return `${day}/${m}`
  }
  const start = fmt(p.start_date)
  const end = fmt(p.end_date)
  if (start && end) return `${start} – ${end}`
  if (end) return `until ${end}`
  if (start) return `from ${start}`
  return '—'
}

/**
 * Build a display label for the MultiSelectDropdown options.
 */
export function promoOptionLabel(p) {
  const discount = p.discount_percent ? `${Math.round(p.discount_percent)}% off` : ''
  return [p.name, discount, promoScope(p)].filter(Boolean).join(' — ')
}

/**
 * Map a promotion DB row to the shape expected by PriceListPdf.
 */
export function promoToPdfRow(p) {
  return {
    name: p.name,
    scope: promoScope(p),
    validity: promoValidity(p),
    discount_percent: p.discount_percent,
  }
}
